import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useUser } from './UserContext';
import { apiFetch, getApiBaseUrl } from '../utils/auth';

const NotificationContext = createContext();

export const useNotifications = () => {
    return useContext(NotificationContext);
};

export const NotificationProvider = ({ children }) => {
    const { currentUser } = useUser();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const socketWarnedRef = useRef(false);

    // Fetch initial notifications
    const fetchNotifications = async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const response = await apiFetch('/notifications');
            const data = await response.json();
            setNotifications(data.notifications || []);
            setUnreadCount(data.unreadCount || 0);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    // Socket.IO — same origin as REST API (getApiBaseUrl). Requires backend running.
    // Set REACT_APP_ENABLE_SOCKET=false to skip (e.g. UI-only dev without API).
    useEffect(() => {
        if (!currentUser) return;

        const disabled =
            process.env.REACT_APP_ENABLE_SOCKET === 'false' ||
            process.env.REACT_APP_ENABLE_SOCKET === '0';
        if (disabled) return;

        const apiBase = getApiBaseUrl();
        const newSocket = io(apiBase, {
            transports: ['websocket', 'polling'],
            reconnectionAttempts: 6,
            reconnectionDelay: 2500,
            reconnectionDelayMax: 12000,
            timeout: 10000
        });

        newSocket.on('connect', () => {
            socketWarnedRef.current = false;
            newSocket.emit('join', currentUser.id);
        });

        newSocket.on('connect_error', () => {
            if (!socketWarnedRef.current && process.env.NODE_ENV === 'development') {
                socketWarnedRef.current = true;
                // eslint-disable-next-line no-console
                console.warn(
                    '[notifications] Socket.IO cannot reach the API at',
                    apiBase,
                    '— start the backend (e.g. port 3001) or set REACT_APP_ENABLE_SOCKET=false to silence retries.'
                );
            }
        });

        newSocket.on('notification', (notification) => {
            setNotifications((prev) => [notification, ...prev]);
            setUnreadCount((prev) => prev + 1);
        });

        return () => {
            newSocket.removeAllListeners();
            newSocket.disconnect();
        };
    }, [currentUser]);

    // Initial fetch when user loads
    useEffect(() => {
        if (currentUser) {
            fetchNotifications();
        } else {
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [currentUser]);

    const markAsRead = async (id) => {
        try {
            // Optimistic update
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            // Recalculate unread count locally
            setUnreadCount(prev => Math.max(0, prev - 1));

            await apiFetch(`/notifications/${id}/read`, { method: 'PUT' });
        } catch (error) {
            console.error('Error marking notification as read:', error);
            fetchNotifications(); // Revert on error
        }
    };

    const markAllAsRead = async () => {
        try {
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
            await apiFetch('/notifications/mark-all-read', { method: 'PUT' });
        } catch (error) {
            console.error('Error marking all as read:', error);
            fetchNotifications();
        }
    };

    const deleteNotification = async (id) => {
        try {
            const wasUnread = notifications.find(n => n.id === id)?.is_read === false;
            setNotifications(prev => prev.filter(n => n.id !== id));
            if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));

            await apiFetch(`/notifications/${id}`, { method: 'DELETE' });
        } catch (error) {
            console.error('Error deleting notification:', error);
            fetchNotifications();
        }
    };

    const value = {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        fetchNotifications
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
